/**
 * Token Manager
 *
 * Frontend wrapper over @shared/http's cookie-based token manager.
 *
 * SECURITY: both credentials live in HttpOnly cookies set by the backend — the
 * short-lived access token and the long-lived, rotating session cookie. JS can
 * read neither. localStorage holds only a non-sensitive "has session" marker
 * (so the client knows when to attempt a refresh) and a stable device_id (to
 * label this device in the "your devices" view).
 */
import {
	createLocalStorageTokenManager,
	type TokenManager,
} from "@shared/http";
import { generateUuid, isValidUuid } from "@shared/utils";

const TOKEN_KEYS = {
	accessTokenKey: "access_token",
	// Now holds a "1" session marker (not a token).
	refreshTokenKey: "refresh_token",
} as const;
const DEVICE_ID_KEY = "device_id";
const AUTH_STORE_KEY = "finishd-auth-store";

const base: TokenManager = createLocalStorageTokenManager(TOKEN_KEYS);

// Access token is in an HttpOnly cookie — JS cannot read it.
export function getAccessToken(): string | null {
	return null;
}

// Whether this device has a session (a non-sensitive marker, not a token).
export function getRefreshToken(): string | null {
	return localStorage.getItem(TOKEN_KEYS.refreshTokenKey);
}

/** Record that this device now has a session (set after a successful login). */
export function markSignedIn(): void {
	localStorage.setItem(TOKEN_KEYS.refreshTokenKey, "1");
}

/**
 * Persist the device id the backend labelled this session with.
 *
 * The backend is authoritative: it echoes the id it stored, and that value is
 * what keeps repeat logins from one browser collapsing into a single device
 * entry (see #286). Reuse it on the next login rather than the locally-minted
 * id, so the two cannot drift apart.
 */
export function setDeviceId(id: string): void {
	localStorage.setItem(DEVICE_ID_KEY, id);
}

export function getDeviceId(): string | null {
	return localStorage.getItem(DEVICE_ID_KEY);
}

/**
 * A stable per-install device id, generated once and reused. It deliberately
 * survives logout so the same browser is recognised as the same device and
 * sent on verify-otp to label the session.
 *
 * A stored value that is not a UUID is regenerated rather than sent as-is: the
 * backend now requires a UUID `deviceId` and answers 400 otherwise, so a
 * corrupt or legacy value would fail every login with no way to recover short
 * of clearing storage by hand.
 */
export function getOrCreateDeviceId(): string {
	let id = localStorage.getItem(DEVICE_ID_KEY);
	if (!id || !isValidUuid(id)) {
		id = generateUuid();
		localStorage.setItem(DEVICE_ID_KEY, id);
	}
	return id;
}

/** Mark the device as signed in. Tokens themselves live in HttpOnly cookies. */
export function setTokens(): void {
	markSignedIn();
}

export function clearTokens(): void {
	// Cookies are cleared by the backend logout response; here we drop the
	// session marker, any stale access key, and the persisted auth store.
	// device_id is intentionally kept so the device stays recognisable.
	localStorage.removeItem(TOKEN_KEYS.refreshTokenKey);
	localStorage.removeItem(TOKEN_KEYS.accessTokenKey);
	localStorage.removeItem(AUTH_STORE_KEY);
}

export function hasAccessToken(): boolean {
	return !!getRefreshToken();
}

export function hasRefreshToken(): boolean {
	return !!getRefreshToken();
}

/**
 * The bare TokenManager the shared api-client expects.
 */
export const frontendTokenManager: TokenManager = {
	getAccessToken: () => null,
	getRefreshToken: base.getRefreshToken,
	setTokens: () => markSignedIn(),
	clearTokens: () => {
		localStorage.removeItem(TOKEN_KEYS.refreshTokenKey);
		localStorage.removeItem(TOKEN_KEYS.accessTokenKey);
		localStorage.removeItem(AUTH_STORE_KEY);
	},
};
